/**
 * ShareResultModal – Share Latest Competition Result as PNG
 * Instagram Story format (9:16 ratio, rendered at 405×720)
 * 4 premium layouts · Multi-result selection · Options toggles
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import html2canvas from "html2canvas";
import { collection, addDoc } from "firebase/firestore";
import { db, auth } from "../firebase";
import "./ShareResultModal.css";

// ── helpers ────────────────────────────────────────────────────
function studioFromResults(results) {
  for (const r of results) {
    if (r.studio && r.studio !== "-" && r.studio !== "") return r.studio;
  }
  return null;
}

async function trackShareEvent(type, dancerName, results) {
  try {
    const user = auth.currentUser;
    await addDoc(collection(db, "shareCardEvents"), {
      type,                       // "generate" | "download"
      dancerName,
      dances: results.map(r => (r.dance ? `${r.category} · ${r.dance}` : r.category)),
      timestamp: new Date(),
      generatedBy: user
        ? { userId: user.uid, email: user.email, name: user.displayName || user.email }
        : null,
    });
  } catch (e) {
    // non-blocking — don't interrupt the user flow
  }
}

function formatResultDate(ts) {
  if (!ts) return "";
  let date;
  if (ts && typeof ts.toDate === "function") date = ts.toDate();
  else if (ts instanceof Date) date = ts;
  else date = new Date(ts);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("el-GR", { day: "numeric", month: "long", year: "numeric" });
}

function getOrdinal(n) {
  if (n <= 20) return ["","1st","2nd","3rd","4th","5th","6th","7th","8th","9th","10th",
    "11th","12th","13th","14th","15th","16th","17th","18th","19th","20th"][n];
  return `${n}th`;
}

// ─────────────────────────────────────────────────────────────────
//  Shared helpers for card layout
// ─────────────────────────────────────────────────────────────────

// Group results by competition; returns array of { compName, date, results[] }
function groupByCompetition(results) {
  const map = new Map();
  for (const r of results) {
    const key = r.competitionName || "—";
    if (!map.has(key)) map.set(key, { compName: key, timestamp: r.timestamp, items: [] });
    map.get(key).items.push(r);
  }
  return Array.from(map.values()).map(g => ({ ...g, items: sortItems(g.items) }));
}

// Latin dances always appear in this fixed order
const LATIN_ORDER = ["Samba", "ChaChaCha", "Rumba", "Pasodoble", "Jive"];
function sortItems(items) {
  return [...items].sort((a, b) => {
    const ai = LATIN_ORDER.indexOf(a.dance || "");
    const bi = LATIN_ORDER.indexOf(b.dance || "");
    if (ai === -1 && bi === -1) return 0; // both non-Latin, keep order
    if (ai === -1) return 1;              // non-Latin after Latin
    if (bi === -1) return -1;
    return ai - bi;
  });
}

// ─────────────────────────────────────────────────────────────────
//  CSS-BASED CARDS  (used for live mini preview only)
// ─────────────────────────────────────────────────────────────────

// Single horizontal result row: [medal/ordinal Place] · [category] · [out of N]
function ResultRow({ result, showParticipants, variant }) {
  const n = parseInt(result.place);
  const catLabel = result.category + (result.dance ? ` · ${result.dance}` : "");
  const hasParticipants = showParticipants && result.participants && result.participants > 0;
  const isTop3 = n >= 1 && n <= 3;
  const medalEmoji = isTop3 ? (n===1?"🥇":n===2?"🥈":"🥉") : null;

  return (
    <div className={`sc-row sc-row--${variant}`}>
      <span className="sc-row__place">
        {isTop3
          ? <span className="sc-row__medal">{medalEmoji}</span>
          : <span className="sc-row__place-num">{getOrdinal(n)}</span>
        }
        <span className="sc-row__place-text">Place</span>
      </span>
      <span className="sc-row__divider">·</span>
      <span className="sc-row__cat">{catLabel}</span>
      {hasParticipants && <><span className="sc-row__divider">·</span><span className="sc-row__parts">out of {result.participants}</span></>}
    </div>
  );
}

function CardInner({ results, dancerName, logoUrl, showParticipants, studioName, variant, accent, extraTop, extraMid, logoExtraClass, avatarUrl, rankingOverview, showRanks }) {
  const groups = groupByCompetition(results);
  // Filter to only ranked categories (rank is a number > 0)
  const ranks = (rankingOverview || []).filter(o => o.currentRank && o.currentRank !== "-" && Number(o.currentRank) > 0);
  return (
    <div className="sc__inner">
      {/* ── TOP HEADER — pinned, always large ─────────────────── */}
      <div className="sc__header-block">
        {groups[0] && (
          <div className={`sc__comp-title sc__comp-title--${variant}`}>
            <div className="sc__comp-title-name">{groups[0].compName}</div>
            <div className="sc__comp-title-date">{formatResultDate(groups[0].timestamp)}</div>
          </div>
        )}
        <div className={`sc__logo-row sc__logo-row--${variant}`}>
          <div className={`sc__logo-deco sc__logo-deco--left sc__logo-deco--${variant}`} />
          <div className="sc__logo-wrap">
            {logoUrl && <img src={logoUrl} alt="" className={`sc__logo${logoExtraClass ? ' ' + logoExtraClass : ''}`} crossOrigin="anonymous" />}
            <span className={`sc__url sc__url--${variant}`}>www.greekranking.gr</span>
          </div>
          <div className={`sc__logo-deco sc__logo-deco--right sc__logo-deco--${variant}`} />
        </div>
        {extraTop}
        <h1 className={`sc__name sc__name--${variant}`} style={{ marginBottom:"0.8rem" }}>{dancerName}</h1>
        {(avatarUrl || studioName) && (
          <div style={{ display:"flex", flexDirection:"row", alignItems:"center", justifyContent:"center", gap:"0.5rem", width:"100%" }}>
            {avatarUrl && (
              <div className={`sc__avatar-wrap sc__avatar--${variant}`}>
                <img src={avatarUrl} alt="" className="sc__avatar" crossOrigin="anonymous" />
              </div>
            )}
            {studioName && <div className={`sc__studio sc__studio--${variant}`} style={{ margin:0, width:"auto", flex:1 }}>{studioName}</div>}
          </div>
        )}
        {extraMid}
      </div>

      {/* ── RESULTS BODY — fills remaining space, centered ─────── */}
      <div className="sc__body-block" style={results.length <= 5 ? {justifyContent:"flex-start", paddingTop:"1.2rem"} : {}}>
        <div className="sc__results-list">
          {groups.map((g, gi) => (
            <div key={gi} className="sc__comp-group">
              {gi > 0 && (
                <div className={`sc__comp-header sc__comp-header--${variant}`}>
                  {g.compName} · {formatResultDate(g.timestamp)}
                </div>
              )}
              {g.items.map((r, ri) => (
                <ResultRow key={ri} result={r} showParticipants={showParticipants} variant={variant} />
              ))}
              {gi < groups.length - 1 && <div className={`sc__sep sc__sep--${variant}`} />}
            </div>
          ))}
        </div>
      </div>

      {/* ── RANK BADGES — absolute bottom corners ──────────────── */}
      {showRanks && ranks.length > 0 && (
        <div className={`sc__ranks sc__ranks--${variant}`}>
          <div className={`sc__ranks-title sc__ranks-title--${variant}`}>Current Rank</div>
          <div className="sc__ranks-grid">
            {ranks.slice(0, 6).map((o, i) => (
              <div key={i} className={`sc__rank-badge sc__rank-badge--${variant}`}>
                <span className="sc__rank-cat">{o.category}{o.dance ? ` ${o.dance}` : ""}</span>
                <span className="sc__rank-div">·</span>
                <span className="sc__rank-num">#{o.currentRank}</span>
                {o.totalDancers > 0 && <><span className="sc__rank-div">·</span><span className="sc__rank-parts">of {o.totalDancers}</span></>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function dcClass(count) {
  if (count >= 8) return "sc--d8";
  if (count >= 6) return "sc--d6";
  if (count >= 4) return "sc--d4";
  if (count === 3) return "sc--d3";
  if (count === 2) return "sc--d2";
  if (count === 1) return "sc--d1";
  return "";
}

function CardAurora({ results, dancerName, logoUrl, showParticipants, studioName, count, avatarUrl, rankingOverview, showRanks }) {
  return (
    <div className={`sc sc--aurora ${dcClass(count)}`}>
      <div className="sc__blob sc__blob--a1" />
      <div className="sc__blob sc__blob--a2" />
      <CardInner results={results} dancerName={dancerName} logoUrl={logoUrl}
        showParticipants={showParticipants} studioName={studioName} variant="aurora"
        extraTop={<div className="sc__hr sc__hr--aurora" />} extraMid={null}
        avatarUrl={avatarUrl} rankingOverview={rankingOverview} showRanks={showRanks} />
    </div>
  );
}

function CardGold({ results, dancerName, logoUrl, showParticipants, studioName, count, avatarUrl, rankingOverview, showRanks }) {
  return (
    <div className={`sc sc--gold ${dcClass(count)}`}>
      <div className="sc__gold-stripe sc__gold-stripe--top" />
      <CardInner results={results} dancerName={dancerName} logoUrl={logoUrl}
        showParticipants={showParticipants} studioName={studioName} variant="gold"
        extraTop={null}
        extraMid={null}
        avatarUrl={avatarUrl} rankingOverview={rankingOverview} showRanks={showRanks} />
      <div className="sc__gold-stripe sc__gold-stripe--bottom" />
    </div>
  );
}

function CardNeon({ results, dancerName, logoUrl, showParticipants, studioName, count, avatarUrl, rankingOverview, showRanks }) {
  return (
    <div className={`sc sc--neon ${dcClass(count)}`}>
      <div className="sc__neon-grid" />
      <CardInner results={results} dancerName={dancerName}
        logoUrl={logoUrl} showParticipants={showParticipants} studioName={studioName} variant="neon"
        extraTop={null}
        extraMid={null}
        logoExtraClass="sc__logo--neon"
        avatarUrl={avatarUrl} rankingOverview={rankingOverview} showRanks={showRanks} />
    </div>
  );
}

function CardRose({ results, dancerName, logoUrl, showParticipants, studioName, count, avatarUrl, rankingOverview, showRanks }) {
  return (
    <div className={`sc sc--rose ${dcClass(count)}`}>
      <div className="sc__rose-blob sc__rose-blob--1" />
      <div className="sc__rose-blob sc__rose-blob--2" />
      <CardInner results={results} dancerName={dancerName} logoUrl={logoUrl}
        showParticipants={showParticipants} studioName={studioName} variant="rose"
        extraTop={null}
        extraMid={null}
        avatarUrl={avatarUrl} rankingOverview={rankingOverview} showRanks={showRanks} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
//  INLINE-STYLED CAPTURE CARD  (used ONLY for html2canvas rendering)
//  100% inline styles → pixel-perfect, no CSS class dependencies
// ─────────────────────────────────────────────────────────────────

// Theme tokens (all px, all explicit)
const THEMES = {
  aurora: {
    bg: "linear-gradient(160deg, #0f0c29 0%, #302b63 55%, #24243e 100%)",
    nameColor: "#ffffff",
    urlColor: "rgba(190,180,255,0.65)",
    hrColor: "linear-gradient(90deg, transparent, rgba(150,120,255,0.8), transparent)",
    medalColor: "#c4b5fd",
    placeTextColor: "#e9d5ff",
    partsColor: "rgba(196,181,253,0.75)",
    catColor: "rgba(255,255,255,0.7)",
    dateColor: "rgba(196,181,253,0.55)",
    sepColor: "rgba(150,120,255,0.5)",
    studioColor: "rgba(196,181,253,0.7)",
    studioBg: "rgba(120,80,255,0.18)",
    studioBorder: "rgba(120,80,255,0.35)",
    accent: null,
  },
  gold: {
    bg: "linear-gradient(175deg, #1a0a00 0%, #2e1800 35%, #1a0a00 100%)",
    nameColor: "#fde68a",
    urlColor: "rgba(201,162,39,0.65)",
    hrColor: "linear-gradient(90deg, transparent, #c9a227, transparent)",
    medalColor: "#fde68a",
    placeTextColor: "#fef3c7",
    partsColor: "rgba(253,230,138,0.65)",
    catColor: "rgba(253,230,138,0.8)",
    dateColor: "rgba(201,162,39,0.55)",
    sepColor: "#c9a227",
    studioColor: "rgba(253,230,138,0.75)",
    studioBg: "rgba(201,162,39,0.1)",
    studioBorder: "rgba(201,162,39,0.3)",
    accent: "linear-gradient(90deg, #7c5200, #c9a227, #f0c040, #c9a227, #7c5200)",
  },
  neon: {
    bg: "#080015",
    nameColor: "#ffffff",
    urlColor: "rgba(0,255,247,0.5)",
    hrColor: null, // uses neon-bar instead
    medalColor: "#00fff7",
    placeTextColor: "#ffffff",
    partsColor: "rgba(0,255,247,0.55)",
    catColor: "rgba(191,95,255,0.85)",
    dateColor: "rgba(0,255,247,0.4)",
    sepColor: "#00fff7",
    studioColor: "rgba(0,255,247,0.65)",
    studioBg: "rgba(0,255,247,0.08)",
    studioBorder: "rgba(0,255,247,0.25)",
    accent: null,
  },
  rose: {
    bg: "linear-gradient(160deg, #fff0f6 0%, #fce4ec 50%, #fdf2f8 100%)",
    nameColor: "#880e4f",
    urlColor: "rgba(173,20,87,0.5)",
    hrColor: "linear-gradient(90deg, transparent, #f48fb1, transparent)",
    medalColor: "#c2185b",
    placeTextColor: "#ad1457",
    partsColor: "rgba(173,20,87,0.6)",
    catColor: "rgba(136,14,79,0.75)",
    dateColor: "rgba(173,20,87,0.5)",
    sepColor: "#f48fb1",
    studioColor: "rgba(173,20,87,0.65)",
    studioBg: "rgba(244,143,177,0.14)",
    studioBorder: "rgba(244,143,177,0.4)",
    accent: null,
  },
};

function CaptureResultRow({ result, showParticipants, theme, d }) {
  const t = THEMES[theme];
  const n = parseInt(result.place);
  const catLabel = result.category + (result.dance ? ` · ${result.dance}` : "");
  const hasParticipants = showParticipants && result.participants && result.participants > 0;
  const isTop3 = n >= 1 && n <= 3;
  const medalEmoji = isTop3 ? (n===1?"🥇":n===2?"🥈":"🥉") : null;
  const divColor = t.sepColor;

  return (
    <div style={{ display:"flex", flexDirection:"row", alignItems:"center", justifyContent:"center",
                  flexWrap:"wrap", gap:`${d.rowGap}px`, width:"100%", boxSizing:"border-box",
                  padding:`${d.rowGap}px 0` }}>
      {/* Medal or ordinal + Place */}
      <span style={{ display:"inline-flex", alignItems:"center", gap:"3px", flexShrink:0 }}>
        {isTop3
          ? <span style={{ fontSize:`${d.medal}px`, lineHeight:1, fontFamily:"Apple Color Emoji,Segoe UI Emoji,Noto Color Emoji,sans-serif" }}>{medalEmoji}</span>
          : <span style={{ fontSize:`${d.ord}px`, fontWeight:900, lineHeight:1, color:t.medalColor }}>{getOrdinal(n)}</span>
        }
        <span style={{ fontSize:`${d.placeText}px`, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.04em", color:t.placeTextColor }}> Place</span>
      </span>
      <span style={{ color:divColor, opacity:0.45, fontSize:`${d.cat}px` }}>·</span>
      {/* Category */}
      <span style={{ fontSize:`${d.cat}px`, fontWeight:600, color:t.catColor, lineHeight:1.2 }}>{catLabel}</span>
      {/* out of N */}
      {hasParticipants && (
        <><span style={{ color:divColor, opacity:0.45, fontSize:`${d.cat}px` }}>·</span>
        <span style={{ fontSize:`${d.parts}px`, fontWeight:500, color:t.partsColor }}>out of {result.participants}</span></>
      )}
    </div>
  );
}

function CaptureCard({ layout, results, dancerName, logoUrl, showParticipants, studioName, avatarUrl, rankingOverview, showRanks }) {
  const t = THEMES[layout];
  const font = "'Segoe UI','Helvetica Neue',Arial,sans-serif";
  const cnt = Math.min(results.length, MAX_RESULTS);
  const d = getBodyDensity(cnt);
  const groups = groupByCompetition(results);

  // Header density: scales down when many results to free vertical space
  const dH = cnt >= 8 ? getDensity(4) : cnt >= 6 ? getDensity(3) : cnt >= 5 ? getDensity(2) : getDensity(1);

  // Filter to ranked categories
  const ranks = (rankingOverview || []).filter(o => o.currentRank && o.currentRank !== "-" && Number(o.currentRank) > 0).slice(0, 6);

  const outer = { width:"405px", height:"720px", position:"relative", overflow:"hidden",
    display:"flex", flexDirection:"column", background:t.bg, fontFamily:font, boxSizing:"border-box" };
  const headerPad = cnt >= 8 ? "12px 20px 8px" : cnt >= 6 ? "16px 24px 10px" : cnt >= 5 ? "18px 24px 12px" : "28px 28px 16px";
  const headerGap = cnt >= 8 ? 4 : cnt >= 6 ? 6 : cnt >= 5 ? 8 : dH.innerGap;
  const headerBlock = {
    position:"relative", zIndex:2, width:"100%", flexShrink:0,
    display:"flex", flexDirection:"column", alignItems:"center",
    padding:headerPad, boxSizing:"border-box",
    gap:`${headerGap}px`, textAlign:"center", fontFamily:font,
  };
  const bodyBlock = {
    position:"relative", zIndex:2, flex:1,
    display:"flex", flexDirection:"column", alignItems:"center", justifyContent: cnt <= 4 ? "flex-start" : "center",
    padding: cnt <= 4 ? `20px 24px ${ranks.length > 0 && showRanks ? "62px" : "24px"}` : `4px 20px ${ranks.length > 0 && showRanks ? "62px" : "24px"}`, boxSizing:"border-box",
    gap:`${d.innerGap}px`, textAlign:"center", fontFamily:font,
    minHeight:0,
  };
  const logoRow   = { display:"flex", flexDirection:"row", alignItems:"center", justifyContent:"center", width:"100%", gap:"10px" };
  const logoDeco  = (side) => {
    const base = { flex:1, height:"2px", borderRadius:"2px", minWidth:"20px", maxWidth:"60px" };
    if (layout==="aurora") return { ...base, background:"linear-gradient(90deg, transparent, rgba(150,120,255,0.75), transparent)" };
    if (layout==="gold")   return { ...base, background:"linear-gradient(90deg, transparent, #c9a227, transparent)" };
    if (layout==="neon")   return { ...base, background:"linear-gradient(90deg, transparent, #00fff7 30%, #bf5fff 70%, transparent)", boxShadow:"0 0 4px #00fff7" };
    if (layout==="rose")   return { ...base, background:"linear-gradient(90deg, transparent, #f48fb1, transparent)" };
    return base;
  };
  const logoWrap  = { display:"flex", flexDirection:"column", alignItems:"center", gap:"4px" };
  const logoImg   = { width:`${dH.logo}px`, height:`${dH.logo}px`, objectFit:"contain", borderRadius:"14px",
    filter:"drop-shadow(0 4px 12px rgba(0,0,0,0.3))" };
  const urlStyle  = { fontSize:"10px", fontWeight:700, letterSpacing:"0.07em", textTransform:"uppercase", color:t.urlColor };
  const nameStyle = { margin:0, fontSize:`${dH.name}px`, fontWeight:900, lineHeight:1.1, letterSpacing:"-0.02em", wordBreak:"break-word", color:t.nameColor, fontFamily:font };
  const hrStyle   = t.hrColor ? { width:`${dH.hrW}px`, height:"2px", borderRadius:"2px", flexShrink:0, background:t.hrColor, border:"none", margin:0 } : null;
  const resultsList = { display:"flex", flexDirection:"column", alignItems:"center", width:"100%", gap:`${d.rowGap}px` };
  // Competition title always large (uses dH)
  const compTitleNameStyle = { fontSize:`${Math.round(dH.name * 0.8)}px`, fontWeight:800, color:t.dateColor,
    letterSpacing:"0.02em", lineHeight:1.25, wordBreak:"break-word" };
  const compTitleDateStyle = { fontSize:`${Math.round(dH.name * 0.5)}px`, fontWeight:600, color:t.dateColor,
    opacity:0.8, letterSpacing:"0.02em", marginTop:"3px" };
  const compHeaderStyle = { fontSize:`${d.date}px`, fontWeight:700, color:t.dateColor, textAlign:"center",
    letterSpacing:"0.01em", paddingBottom:`${d.rowGap}px`, width:"100%" };
  const sepStyle  = { width:`${d.hrW}px`, height:"1px", borderRadius:"2px", background:t.sepColor,
    opacity: layout==="neon" ? 0.7 : 0.3, margin:`${d.sepMar}px auto` };
  const studioStyle = { fontSize:`${Math.max(9,d.date)}px`, fontWeight:700, textTransform:"uppercase",
    letterSpacing:"0.1em", color:t.studioColor, background:t.studioBg,
    border:`1px solid ${t.studioBorder}`, borderRadius:"20px", padding:"4px 14px",
    marginTop:"6px", textAlign:"center", display:"block",
    width:"calc(100% - 3rem)", boxSizing:"border-box", marginLeft:"auto", marginRight:"auto" };
  const neonBarStyle = { width:"100%", height:"2px", flexShrink:0,
    background:"linear-gradient(90deg, transparent 0%, #00fff7 20%, #bf5fff 50%, #00fff7 80%, transparent 100%)",
    boxShadow:"0 0 6px #00fff7" };

  return (
    <div style={outer}>
      {layout==="gold" && <div style={{ height:"6px", background:t.accent, flexShrink:0, zIndex:3 }} />}

      {/* ── HEADER — competition title + logo + dancer name ──── */}
      <div style={headerBlock}>
        {groups[0] && (
          <div style={{ textAlign:"center", width:"100%" }}>
            <div style={compTitleNameStyle}>{groups[0].compName}</div>
            <div style={compTitleDateStyle}>{formatResultDate(groups[0].timestamp)}</div>
          </div>
        )}
        <div style={logoRow}>
          <div style={logoDeco("left")} />
          <div style={logoWrap}>
            {logoUrl && <img src={logoUrl} alt="" style={logoImg} crossOrigin="anonymous" />}
            <span style={urlStyle}>www.greekranking.gr</span>
          </div>
          <div style={logoDeco("right")} />
        </div>
        {layout==="aurora" && hrStyle && <div style={hrStyle} />}
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"6px" }}>
          <div style={{...nameStyle, marginBottom:"12px"}}>{dancerName}</div>
          {cnt <= 5 ? (
            /* ≤ 5 results: studio pill on top, big avatar below */
            <>
              {studioName && <div style={{...studioStyle, marginTop:0, alignSelf:"center"}}><span style={{ position:"relative", top:"-8px" }}>{studioName}</span></div>}
              {avatarUrl && (
                <div style={{ width:"80px", borderRadius:"14px", overflow:"hidden", flexShrink:0, marginTop:"4px",
                  border:`3px solid ${t.nameColor}`, boxShadow:`0 0 0 2px ${t.studioBorder}, 0 6px 20px rgba(0,0,0,0.4)` }}>
                  <img src={avatarUrl} alt="" crossOrigin="anonymous"
                    style={{ width:"100%", height:"auto", display:"block" }} />
                </div>
              )}
            </>
          ) : (
            /* > 5 results: compact side-by-side */
            (avatarUrl || studioName) && (
              <div style={{ display:"flex", flexDirection:"row", alignItems:"center", justifyContent:"center", gap:"6px", width:"100%" }}>
                {avatarUrl && (
                  <div style={{ width:"44px", borderRadius:"10px", overflow:"hidden", flexShrink:0,
                    border:`2.5px solid ${t.nameColor}`, boxShadow:`0 0 0 2px ${t.studioBorder}, 0 4px 16px rgba(0,0,0,0.35)` }}>
                    <img src={avatarUrl} alt="" crossOrigin="anonymous"
                      style={{ width:"100%", height:"auto", display:"block" }} />
                  </div>
                )}
                {studioName && <div style={{...studioStyle, marginTop:0, flex:1}}><span style={{ position:"relative", top:"-8px" }}>{studioName}</span></div>}
              </div>
            )
          )}
        </div>
      </div>

      {/* ── BODY — results, vertically centered in remaining space */}
      <div style={bodyBlock}>
        <div style={resultsList}>
          {groups.map((g, gi) => (
            <div key={gi} style={{ width:"100%" }}>
              {gi > 0 && <div style={compHeaderStyle}>{g.compName} · {formatResultDate(g.timestamp)}</div>}
              {g.items.map((r, ri) => (
                <CaptureResultRow key={ri} result={r} showParticipants={showParticipants} theme={layout} d={d} />
              ))}
              {gi < groups.length - 1 && <div style={sepStyle} />}
            </div>
          ))}
        </div>
      </div>

      {/* ── RANK BADGES — absolute bottom corners, 2-column grid */}
      {showRanks && ranks.length > 0 && (
        <div style={{
          position:"absolute", bottom: layout==="gold" ? "14px" : "10px",
          left:"10px", right:"10px", zIndex:4,
          display:"flex", flexDirection:"column", alignItems:"center",
          gap:"3px", fontFamily:font,
        }}>
          {/* Title */}
          <div style={{
            fontSize:"7px", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.12em",
            color:t.urlColor, opacity:0.85, marginBottom:"1px",
          }}>Current Rank</div>
          {/* Grid */}
          <div style={{
            width:"100%",
            display:"grid",
            gridTemplateColumns: ranks.length === 1 ? "1fr" : "1fr 1fr",
            gap:"3px",
          }}>
            {ranks.map((o, i) => {
              const rankColor = Number(o.currentRank) <= 3 ? "#fbbf24" : Number(o.currentRank) <= 10 ? t.medalColor : t.nameColor;
              const subColor = t.catColor;
              const divColor = t.sepColor;
              return (
                <div key={i} style={{
                  display:"flex", flexDirection:"row", alignItems:"center", justifyContent:"center",
                  flexWrap:"wrap", gap:"3px",
                  background: layout==="neon" ? "rgba(0,255,247,0.07)" : layout==="rose" ? "rgba(244,143,177,0.14)" : "rgba(255,255,255,0.07)",
                  border:`1px solid ${t.studioBorder}`,
                  borderRadius:"5px", padding:"4px 5px 2px", boxSizing:"border-box",
                  backdropFilter:"blur(4px)",
                }}>
                  <span style={{ fontSize:"8px", fontWeight:600, color:subColor, letterSpacing:"0.02em", textAlign:"center", position:"relative", top:"-5px" }}>
                    {o.category}{o.dance ? ` ${o.dance}` : ""}
                  </span>
                  <span style={{ fontSize:"8px", color:divColor, opacity:0.4, flexShrink:0 }}>·</span>
                  <span style={{ fontSize:"8px", fontWeight:900, color:rankColor, flexShrink:0, position:"relative", top:"-5px" }}>#{o.currentRank}</span>
                  {o.totalDancers > 0 && (
                    <><span style={{ fontSize:"8px", color:divColor, opacity:0.4, flexShrink:0, position:"relative", top:"-5px" }}>·</span>
                    <span style={{ fontSize:"8px", fontWeight:500, color:subColor, opacity:0.75, flexShrink:0, position:"relative", top:"-5px" }}>of {o.totalDancers}</span></>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {layout==="gold" && <div style={{ height:"6px", background:t.accent, flexShrink:0, zIndex:3 }} />}
    </div>
  );
}

// ── Layout registry ─────────────────────────────────────────────
const LAYOUTS = [
  { id: "aurora", label: "Aurora",   description: "Dark & dreamy",   Component: CardAurora },
  { id: "gold",   label: "Prestige", description: "Golden elegance", Component: CardGold   },
  { id: "neon",   label: "Neon",     description: "Midnight glow",   Component: CardNeon   },
  { id: "rose",   label: "Rose",     description: "Marble & rose",   Component: CardRose   },
];

// Max results allowed on a card
const MAX_RESULTS = 10;

// Returns px size tokens for a given result count
function getDensity(count) {
  // logo, name, medal, ord, placeText, parts, cat, date, innerGap, rowGap, innerPad, hrW, sepMar
  if (count <= 1)  return { logo:80,  name:36, medal:44, ord:36, placeText:22, parts:20, cat:24, date:15, innerGap:14, rowGap:14, innerPad:32, hrW:60, sepMar:8  };
  if (count === 2) return { logo:72,  name:32, medal:38, ord:30, placeText:19, parts:17, cat:21, date:14, innerGap:12, rowGap:12, innerPad:28, hrW:54, sepMar:6  };
  if (count === 3) return { logo:62,  name:28, medal:32, ord:25, placeText:16, parts:15, cat:18, date:13, innerGap:10, rowGap:10, innerPad:24, hrW:48, sepMar:5  };
  if (count === 4) return { logo:54,  name:25, medal:24, ord:19, placeText:13, parts:12, cat:15, date:12, innerGap:8,  rowGap:7,  innerPad:20, hrW:42, sepMar:4  };
  if (count === 5) return { logo:38,  name:18, medal:16, ord:13, placeText:10, parts:9,  cat:11, date:10, innerGap:5,  rowGap:4,  innerPad:14, hrW:30, sepMar:3  };
  if (count === 6) return { logo:34,  name:16, medal:14, ord:11, placeText:9,  parts:8,  cat:10, date:9,  innerGap:4,  rowGap:3,  innerPad:12, hrW:26, sepMar:3  };
  if (count === 7) return { logo:30,  name:14, medal:12, ord:10, placeText:8,  parts:7,  cat:9,  date:8,  innerGap:3,  rowGap:2,  innerPad:10, hrW:24, sepMar:2  };
  if (count === 8) return { logo:28,  name:13, medal:11, ord:9,  placeText:7,  parts:7,  cat:8,  date:7,  innerGap:3,  rowGap:2,  innerPad:8,  hrW:22, sepMar:2  };
  if (count === 9) return { logo:26,  name:12, medal:10, ord:8,  placeText:7,  parts:6,  cat:7,  date:7,  innerGap:2,  rowGap:1,  innerPad:8,  hrW:20, sepMar:2  };
  /* 10 */         return { logo:24,  name:11, medal:10, ord:8,  placeText:6,  parts:6,  cat:7,  date:6,  innerGap:2,  rowGap:1,  innerPad:8,  hrW:18, sepMar:1  };
}

// For the result rows body: when count is very small (≤5), cap text sizes so
// they don't overflow the card alongside the large header + avatar.
function getBodyDensity(count) {
  // For >5 results the normal density already shrinks appropriately.
  // For ≤5 we cap sizes to roughly the count=5 level for text/row values.
  if (count <= 1)  return { ...getDensity(1),  medal:28, ord:22, placeText:13, parts:13, cat:16, rowGap:10, sepMar:5 };
  if (count === 2) return { ...getDensity(2),  medal:26, ord:20, placeText:13, parts:13, cat:15, rowGap:8,  sepMar:5 };
  if (count === 3) return { ...getDensity(3),  medal:24, ord:18, placeText:13, parts:12, cat:14, rowGap:7,  sepMar:4 };
  if (count === 4) return { ...getDensity(4),  medal:22, ord:17, placeText:12, parts:12, cat:13, rowGap:6,  sepMar:4 };
  if (count === 5) return getDensity(5);
  return getDensity(count); // 6+ unchanged
}

// Card dimensions: 9:16 Instagram Story ratio
const CARD_W = 405;
const CARD_H = 720;

// ── Main Modal ──────────────────────────────────────────────────
export default function ShareResultModal({ isOpen, onClose, recentResults: rawResults, dancerName, canGenerate, profilePictureUrl, rankingOverview }) {
  const recentResults = React.useMemo(() => sortItems(rawResults || []), [rawResults]);
  const [selectedIdxs, setSelectedIdxs]       = useState([]);
  const [selectedLayout, setSelectedLayout]   = useState("aurora");
  const [showParticipants, setShowParticipants] = useState(true);
  const [showStudio, setShowStudio]           = useState(true);
  const [showRanks, setShowRanks]             = useState(true);
  const [isGenerating, setIsGenerating]       = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
  const [logoUrl, setLogoUrl]                 = useState(null);
  const [avatarUrl, setAvatarUrl]             = useState(null);
  const cardRef = useRef(null);

  // Preload logo as base64 for html2canvas
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width  = img.naturalWidth  || 256;
        c.height = img.naturalHeight || 256;
        c.getContext("2d").drawImage(img, 0, 0);
        setLogoUrl(c.toDataURL("image/png"));
      } catch {
        setLogoUrl("/greek-ranking-logo.webp");
      }
    };
    img.onerror = () => setLogoUrl("/greek-ranking-logo.webp");
    img.src = "/greek-ranking-logo.webp";
  }, []);

  // Preload profile picture as base64 for html2canvas
  useEffect(() => {
    if (!profilePictureUrl) { setAvatarUrl(null); return; }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width  = img.naturalWidth  || 256;
        c.height = img.naturalHeight || 256;
        c.getContext("2d").drawImage(img, 0, 0);
        setAvatarUrl(c.toDataURL("image/png"));
      } catch {
        setAvatarUrl(profilePictureUrl);
      }
    };
    img.onerror = () => setAvatarUrl(null);
    img.src = profilePictureUrl;
  }, [profilePictureUrl]);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      // Auto-select only the first MAX_RESULTS results
      const autoIdxs = (recentResults || []).slice(0, MAX_RESULTS).map((_, i) => i);
      setSelectedIdxs(autoIdxs);
      setSelectedLayout("aurora");
      setShowParticipants(true);
      setShowStudio(true);
      setShowRanks(true);
      setGeneratedImageUrl(null);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedResults = (recentResults || []).filter((_, i) => selectedIdxs.includes(i));

  // Derive studio from the results data itself (studio field on each result object)
  const studioName = studioFromResults(recentResults || []);

  const toggleResult = (idx) => {
    setSelectedIdxs(prev => {
      if (prev.includes(idx)) {
        return prev.length > 1 ? prev.filter(i => i !== idx) : prev;
      }
      if (prev.length >= MAX_RESULTS) return prev; // cap
      return [...prev, idx].sort((a, b) => a - b);
    });
    setGeneratedImageUrl(null);
  };

  const generateImage = useCallback(async () => {
    if (!cardRef.current || !selectedResults.length) return;
    setIsGenerating(true);
    setGeneratedImageUrl(null);
    try {
      await new Promise(r => setTimeout(r, 150));
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: null,
        logging: false,
        width:  CARD_W,
        height: CARD_H,
      });
      setGeneratedImageUrl(canvas.toDataURL("image/png"));
      trackShareEvent("generate", dancerName, selectedResults);
    } catch (err) {
      console.error("Share card error:", err);
      alert("Failed to generate image. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  }, [selectedResults, selectedLayout, showParticipants]);

  const downloadImage = () => {
    if (!generatedImageUrl) return;
    const a  = document.createElement("a");
    a.href   = generatedImageUrl;
    a.download = `${dancerName.replace(/\s+/g, "_")}_result.png`;
    a.click();
    trackShareEvent("download", dancerName, selectedResults);
  };

  const shareImage = async () => {
    if (!generatedImageUrl) return;
    const res  = await fetch(generatedImageUrl);
    const blob = await res.blob();
    const file = new File([blob], `${dancerName.replace(/\s+/g, "_")}_result.png`, { type: "image/png" });
    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: `${dancerName} – Competition Result`, text: "www.greekranking.gr 🏆" });
        return;
      } catch (e) {
        if (e.name === "AbortError") return;
      }
    }
    downloadImage();
  };

  if (!isOpen || !recentResults?.length) return null;

  const { Component: LayoutCard } = LAYOUTS.find(l => l.id === selectedLayout) || LAYOUTS[0];
  const stepBase = recentResults.length > 1 ? 1 : 0;

  return (
    <div className="srm-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="srm">

        {/* Header */}
        <div className="srm__header">
          <div className="srm__header-row">
            <div>
              <h2 className="srm__title">🎉 Share Your Result!</h2>
              <p className="srm__subtitle">Story-ready card for Instagram & social media</p>
            </div>
            <button className="srm__close" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="srm__body">

          {/* Step 1 – Select results (only if >1) */}
          {recentResults.length > 1 && (
            <section className="srm__section">
              <h3 className="srm__step">
                <span className="srm__step-badge">1</span>
                Choose result(s) to include
                <span className="srm__hint">Max {MAX_RESULTS}</span>
              </h3>
              {recentResults.length > MAX_RESULTS && (
                <p className="srm__cap-hint">Select up to {MAX_RESULTS} results — card auto-scales to fit.</p>
              )}
              <div className="srm__chips">
                {recentResults.map((r, idx) => {
                  const active = selectedIdxs.includes(idx);
                  return (
                    <button
                      key={idx}
                      className={`srm__chip ${active ? "srm__chip--on" : ""}`}
                      onClick={() => toggleResult(idx)}
                    >
                      <span className="srm__chip-check">{active ? "✓" : ""}</span>
                      <span className="srm__chip-medal">#{r.place}</span>
                      <span className="srm__chip-text">
                        <strong>{r.category}{r.dance ? ` · ${r.dance}` : ""}</strong>
                        <small>{r.competitionName}</small>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Step 2 – Options */}
          <section className="srm__section">
            <h3 className="srm__step">
              <span className="srm__step-badge">{1 + stepBase}</span>
              Options
            </h3>
            <label className="srm__toggle-row">
              <span>Show number of participants</span>
              <button
                className={`srm__toggle ${showParticipants ? "srm__toggle--on" : ""}`}
                onClick={() => { setShowParticipants(p => !p); setGeneratedImageUrl(null); }}
                aria-label="Toggle participants"
              >
                <span className="srm__toggle-knob" />
              </button>
            </label>
            {studioName && (
              <label className="srm__toggle-row">
                <span>Show studio name</span>
                <button
                  className={`srm__toggle ${showStudio ? "srm__toggle--on" : ""}`}
                  onClick={() => { setShowStudio(s => !s); setGeneratedImageUrl(null); }}
                  aria-label="Toggle studio name"
                >
                  <span className="srm__toggle-knob" />
                </button>
              </label>
            )}
            {(rankingOverview || []).some(o => o.currentRank && o.currentRank !== "-" && Number(o.currentRank) > 0) && (
              <label className="srm__toggle-row">
                <span>Show current ranks</span>
                <button
                  className={`srm__toggle ${showRanks ? "srm__toggle--on" : ""}`}
                  onClick={() => { setShowRanks(r => !r); setGeneratedImageUrl(null); }}
                  aria-label="Toggle current ranks"
                >
                  <span className="srm__toggle-knob" />
                </button>
              </label>
            )}
          </section>

          {/* Step 3 – Layout */}
          <section className="srm__section">
            <h3 className="srm__step">
              <span className="srm__step-badge">{2 + stepBase}</span>
              Choose a layout
            </h3>
            <div className="srm__layouts">
              {LAYOUTS.map(l => (
                <button
                  key={l.id}
                  className={`srm__layout srm__layout--${l.id} ${selectedLayout === l.id ? "srm__layout--active" : ""}`}
                  onClick={() => { setSelectedLayout(l.id); setGeneratedImageUrl(null); }}
                >
                  <span className="srm__layout-name">{l.label}</span>
                  <span className="srm__layout-desc">{l.description}</span>
                </button>
              ))}
            </div>
          </section>

          {/* Step 4 – Preview & Generate */}
          <section className="srm__section">
            <h3 className="srm__step">
              <span className="srm__step-badge">{3 + stepBase}</span>
              Preview &amp; Save
            </h3>

            {/* Off-screen render target — uses fully inline-styled CaptureCard */}
            <div className="srm__offscreen" aria-hidden="true">
              <div ref={cardRef} style={{ width: CARD_W, height: CARD_H, overflow: "hidden" }}>
                {selectedResults.length > 0 && (
                  <CaptureCard
                    layout={selectedLayout}
                    results={selectedResults}
                    dancerName={dancerName}
                    logoUrl={logoUrl}
                    showParticipants={showParticipants}
                    studioName={showStudio ? studioName : null}
                    avatarUrl={avatarUrl}
                    rankingOverview={rankingOverview || []}
                    showRanks={showRanks}
                  />
                )}
              </div>
            </div>

            {generatedImageUrl ? (
              <div className="srm__preview">
                <img src={generatedImageUrl} alt="Generated card" className="srm__preview-img" />
                <div className="srm__actions">
                  <button className="srm__btn srm__btn--ghost" onClick={() => setGeneratedImageUrl(null)}>↩ Edit</button>
                  <button className="srm__btn srm__btn--dark"  onClick={downloadImage}>⬇ Download</button>
                  <button className="srm__btn srm__btn--share" onClick={shareImage}>📤 Share</button>
                </div>
              </div>
            ) : (
              <div className="srm__gen-area">
                {/* Live mini preview */}
                {selectedResults.length > 0 && (
                  <div className="srm__mini">
                    <div className="srm__mini-inner">
                      <LayoutCard
                        results={selectedResults}
                        dancerName={dancerName}
                        logoUrl={logoUrl}
                        showParticipants={showParticipants}
                        studioName={showStudio ? studioName : null}
                        count={selectedResults.length}
                        avatarUrl={avatarUrl}
                        rankingOverview={rankingOverview || []}
                        showRanks={showRanks}
                      />
                    </div>
                  </div>
                )}
                {canGenerate ? (
                  <button
                    className="srm__btn srm__btn--generate"
                    onClick={generateImage}
                    disabled={isGenerating || !selectedResults.length}
                  >
                    {isGenerating ? "⏳ Generating…" : "✨ Generate Image"}
                  </button>
                ) : (
                  <div className="srm__locked">
                    <span className="srm__locked-icon">🔒</span>
                    <p className="srm__locked-text">Image generation is available only to verified dancers with a claimed profile.</p>
                  </div>
                )}
              </div>
            )}
          </section>

        </div>
      </div>
    </div>
  );
}
